import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/functions'

import router from '@/router'
import { zapierSignUpHook } from '@/utils/zapier'
import { signupAction, signinAction, resetPasswordAction, logoutAction, emailVerifiedAction } from '@/utils/logsHelpers'

import { functions } from '@/firebase'
import { MFA_STATES } from '@/config/config'

export default {
  namespaced: true,
  state: {
    authUser: null,
    user: null,
    userRef: null,
    loginIntent: null,
    source: null,
    enterprise: null,
    enterpriseRef: null,
    usageStats: {
      projects: 0,
      leads: 0,
      views: 0
    },
    mfaVerificationId: null,
    mfaResolver: null,
    accountIsSuspicious: false
  },
  getters: {
    user (state) {
      if (state.user) {
        if (!state.user.usage_stats) {
          state.user.usage_stats = {
            projects: 0,
            emails: 0
          }
        }

        if (!state.user.plan_details) {
          state.user.plan_details = {
            projectsLimit: 0,
            emailsLimit: 0
          }
        }
      }

      return state.user
    },
    authUser (state) {
      return state.authUser
    },
    isAuthenticated (state) {
      return state.user != null
    },
    loginIntent (state) {
      return state.loginIntent
    },
    source (state) {
      return state.source
    },
    enterprise (state) {
      return state.enterprise
    },
    getUsageStats (state) {
      return state.user && state.user.usage_stats
    },
    getMfaVerificationId (state) {
      return state.mfaVerificationId
    },
    getMfaResolver (state) {
      return state.mfaResolver
    },
    accountIsSuspicious (state) {
      return state.accountIsSuspicious
    }
  },
  mutations: {
    setSuspiciousAccountStatus (state, isAccountSuspicous) {
      state.accountIsSuspicious = isAccountSuspicous
    },
    setLoginIntent (state, payload) {
      state.loginIntent = payload
    },
    setSource (state, payload) {
      state.source = payload
    },
    setUser (state, payload) {
      state.user = payload
    },
    setAuthUser (state, payload) {
      state.authUser = payload
    },
    setUserRef (state, payload) {
      state.userRef = payload
    },
    noLocalUser (state) { // explicitly mark that user is not logged in
      state.authUser = false
      state.user = false
    },
    setEnterprise (state, enterprise) {
      state.enterprise = enterprise
    },
    setEnterpriseRef (state, ref) {
      state.enterpriseRef = ref
    },
    // Billing-related user state changes
    setSubscriptionCancelling (state, getters, isCancelling) {
      if (state.user) {
        if (state.user.enterprise) {
          if (state.enterprise && state.enterprise.billing && state.enterprise.billing.sub) state.enterprise.billing.sub.cancelling = isCancelling
        } else {
          if (state.user.billing && state.user.billing.sub) state.user.billing.sub.cancelling = isCancelling
        }
      }
    },
    setSubscriptionSwitching (state) {
      if (state.user) {
        if (state.user.enterprise) {
          if (state.enterprise && state.enterprise.billing && state.enterprise.billing.sub) state.enterprise.billing.sub.switching = true
        } else {
          if (state.user.billing && state.user.billing.sub) state.user.billing.sub.switching = true
        }
      }
    },
    setSubscriptionIssueResolved (state) {
      if (state.user) {
        if (state.user.enterprise) {
          if (state.enterprise && state.enterprise.billing && state.enterprise.billing.sub) {
            state.enterprise.billing.sub.error = null
            state.enterprise.billing.sub.action_required = null
          }
        } else {
          if (state.user.billing && state.user.billing.sub) {
            state.user.billing.sub.error = null
            state.user.billing.sub.action_required = null
          }
        }
      }
    },
    // Temporarily update user's subscription locally, pending confirmation via Stripe/Firebase
    setUserSubscription (state, planId) {
      if (state.user) {
        if (state.user.enterprise) {
          if (state.enterprise && state.enterprise.billing) {
            state.enterprise.billing.sub = {
              plan: planId,
              pending: true
            }
          }
        } else {
          if (state.user.billing) {
            state.user.billing.sub = {
              plan: planId,
              pending: true
            }
          }
        }
      }
    },
    setMfaVerificationId (state, id) {
      state.mfaVerificationId = id
    },
    setMfaResolver (state, resolver) {
      state.mfaResolver = resolver
    }
  },
  actions: {
    async validateReCaptcha ({}, { token }) {
      const verificationResult = await functions.httpsCallable('validateReCaptcha')({ token })
      return verificationResult?.data
    },

    async closeWelcomeNote ({ state }) {
      const user = state.user

      await firebase
        .database()
        .ref(`users/${user.uid}`)
        .update({
          close_welcome_note: true
        })
    },
    async authWithGoogle ({ dispatch, commit, getters }, { source, lmData }) {
      const gAuthProvider = new firebase.auth.GoogleAuthProvider()

      try {
        const authResult = await firebase.auth().signInWithPopup(gAuthProvider)
        const userUID = authResult.user.uid
        const userInfo = authResult.additionalUserInfo
        const setSource = getters.source

        const userData = {
          name: userInfo.profile.name,
          username: userInfo.profile.name,
          source: source || setSource,
          training_opt_out: false,
          goals: { 'sign-up': true },
          lm_data: lmData || false,
          provider: userInfo.providerId,
          usage_stats: {
            projects: 0,
            captured_emails: 0,
            views: 0
          }
        }
        let storedUserData = {}

        if (userInfo.isNewUser) {
          // ENTERPRISE CHECK HERE
          const userEnterprise = await functions.httpsCallable('mfa-checkIfUseIsEnterprise')({ email: userInfo.profile.email })
          //
          if (userEnterprise.data && userEnterprise.data.mfaState) {
            userData.mfaState = MFA_STATES.PENDING
            router.push('/auth/setup-mfa')
          }
          const newUserRef = firebase.database().ref('users').child(userUID)
          await newUserRef.set(userData)
          signupAction({
            username: userData.username,
            email: userInfo.profile.email,
            uid: userUID
          })
          await zapierSignUpHook(userInfo.profile.email, source || setSource)
          dispatch('checkForAssiciatedDiscounts', { email: userInfo.profile.email, userUID: userUID })
        } else {
          const storedUserDataSnapshot = await firebase.database().ref('users').child(userUID).once('value')
          storedUserData = storedUserDataSnapshot.val()
          commit('setSuspiciousAccountStatus', storedUserData?.isSuspiciousAccount)
          
          if (storedUserData?.isSuspiciousAccount) {
            dispatch('userSignOut')
            throw new Error({
              message: 'Access denied',
              code: '403'
            })
          } 
        }

        localStorage.setItem('user_email', userInfo.profile.email)
        commit('setUser', { ...userData, uid: userUID })

        return Promise.resolve({ ...userData, newUser: userInfo.isNewUser, uid: userUID, email: userInfo.profile.email })
      } catch (err) {
        /** MFA PART */
        let resolver
        if (err.code === 'auth/multi-factor-auth-required') {
          // The user is a multi-factor user. Second factor challenge is required.
          resolver = err.resolver
          return {
            status: 'MFA',
            resolver
          }
        } else {
          throw new Error(err)
        }
      }
    },
    // USER ACTIONS
    async userLogin ({ state, commit, dispatch }, { email, password }) {
      try {
        const { user } = await firebase.auth().signInWithEmailAndPassword(email, password)
        const userSnap = await firebase.database().ref(`users/${user.uid}`).once('value')
        const userData = userSnap.val()

        commit('setSuspiciousAccountStatus', userData?.isSuspiciousAccount)          
        if (userData?.isSuspiciousAccount) {
          dispatch('userSignOut')
          throw new Error({
            message: 'Access denied',
            code: '403'
          })
        }

        signinAction({ email: user.email, uid: user.uid, username: '' })

        if (userData.mfaState === MFA_STATES.PENDING) {
          router.push('/auth/setup-mfa')
        } else if (state.loginIntent) {
          router.push(state.loginIntent)
        } else {
          router.push({ name: 'dashboard' })
        }
        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      } catch (error) {
        let resolver

        if (error.code === 'auth/multi-factor-auth-required') {
          // The user is a multi-factor user. Second factor challenge is required.
          resolver = error.resolver

          return {
            status: 'MFA',
            resolver
          }
        }

        dispatch('clearUser')
        signinAction({ email, username: '', uid: '' }, false)
        return Promise.reject(new Error(error))
      }
    },

    async userJoin ({ dispatch, commit, getters }, { name, username, email, password, source, lmData }) {
      try {
        const { user } = await firebase
          .auth()
          .createUserWithEmailAndPassword(email, password)

        const setSource = getters.source
        await firebase
          .database()
          .ref('users')
          .child(user.uid)
          .set({
            name,
            username,
            source: source || setSource,
            training_opt_out: false,
            goals: { 'sign-up': true },
            lm_data: lmData || false,
            usage_stats: {
              projects: 0,
              captured_emails: 0,
              views: 0
            }
          })

        functions.httpsCallable('sendWelcomeEmailOnCall')({ email, hostName: window.location.hostname })

        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)

        signupAction({ username, email, uid: user.uid })
        await zapierSignUpHook(email, source)

        dispatch('checkForAssiciatedDiscounts', { email: email, userUID: user.uid })

        localStorage.setItem('user_email', email)
        commit('setUser', { ...user, name, username, training_opt_out: false })
        return Promise.resolve(user.uid)
      } catch (error) {
        dispatch('clearUser')
        signupAction({ username, email, uid: '' }, false)
        return Promise.reject(error)
      }
    },
    // Loads the user object from Firebase, and subscribes to updates to keep in sync throughout the session
    async loadUser ({ state, commit, dispatch }, { authUser }) {
      if (state.userRef) {
        // Make sure we only register one value listener at a time
        state.userRef.off()
      }
      const userRef = firebase.database().ref(`users/${authUser.uid}`)
      commit('setUserRef', userRef)
      // **important**
      // Should use .on('value') to register a listener for ongoing updates
      // Required to make sure we catch all billing and subscription changes
      userRef.on('value', (snapshot) => {
        if (snapshot && snapshot.val()) {
          const user = snapshot.val()
          commit('setUser', { ...authUser, ...user })

          if (user.enterprise) dispatch('loadEnterprise')

          // update plan data for users that don't have it
          if (!user.plan_details) {
            functions.httpsCallable('updateUserUsageLimits')({ userId: authUser.uid })
          }

          if (user.shopify) dispatch('shopify/loadShops', null, { root: true })
        }
      })
    },
    // MFA State updates
    async updateUserMFAStatus ({ state }, status) {
      const user = state.user
      const userRef = firebase.database().ref(`/users/${user.uid}`)
      await userRef.update({
        mfaState: status
      })
      return 'updated'
    },
    async cancelUsersMFA ({ state, commit }) {
      try {
        const user = state.user
        await functions.httpsCallable('mfa-cancelUsersMFA')({ uid: user.uid })

        commit('setUser', {
          ...user,
          multiFactor: null
        })

        return 'canceled'
      } catch (err) {
        throw new Error(err)
      }
    },

    async verifyEmail ({ }, { code }) {
      try {
        const result = await firebase.auth().applyActionCode(code)
        emailVerifiedAction({ email: localStorage.getItem('user_email'), username: '', uid: '' }, result)
        return { ...result, verificationStatus: 'success' }
      } catch (err) {
        return { verificationStatus: 'failed' }
      }
    },

    async isEmailDomainSuspended ({}, { email }) {
      if (email) {
        const checkResult = await functions.httpsCallable('admin-isEmailDomainSuspended')({ email })
        return checkResult?.data?.emailIsSuspended
      }
      return false
    },

    async resendEmailConfirmation ({ }, { email }) {
      if (email) {
        return functions.httpsCallable('resendConfirmationEmail')({ email, hostName: window.location.hostname })
      }
    },
    async sendResetPassword ({}, { email }) {
      if (email) {
        return functions.httpsCallable('resetPasswordEmail')({ email, hostName: window.location.hostname })
      }
    },
    async resetPassword (context, { code, password }) {
      const resetUserEmail = await firebase.auth().verifyPasswordResetCode(code)
      const confirmResult = await firebase.auth().confirmPasswordReset(code, password)

      resetPasswordAction({ email: resetUserEmail, uid: '', username: '' })

      return { result: confirmResult, email: resetUserEmail }
    },
    async userSignOut ({ state, dispatch }) {
      const user = state.user
      try {
        await firebase
          .auth()
          .signOut()

        logoutAction(user)
        console.log({
          user,
          mes: 'Log out action'
        })
        dispatch('clearUser')
        
        router.push({ name: 'signin' })
      } catch (error) {
        dispatch('clearUser')
        logoutAction(user, false)
        router.push({ name: 'signin' })
      }
    },
    clearUser ({ state, commit }) {
      if (state.userRef) {
        state.userRef.off()
      }
      commit('setUser', null)
      commit('setUserRef', null)
      commit('setAuthUser', null)
    },
    async updateUser ({ state, commit }, { uid, data }) {
      const id = uid || state.user.uid

      await firebase
        .database()
        .ref(`users/${id}`)
        .update({ ...data })

      commit('setUser', { ...state.user, ...data })
    },
    async grantOAuth (context, payload) {
      const result = await functions.httpsCallable('oauth2-authorize')({
        app: payload.app,
        client: {
          id: payload.client_id
        },
        scopes: payload.scopes
      })
      if (result && result.data && result.data.code) return result.data.code
    },
    async enableTraining ({ dispatch }) {
      await dispatch('updateUser', {
        data: { training_opt_out: false }
      })
    },
    async disableTraining ({ dispatch }) {
      await dispatch('updateUser', {
        data: { training_opt_out: true }
      })
    },
    toggleAdmin ({ state, commit }, payload = true) {
      const adminUser = {
        ...state.user,
        roles: { admin: payload }
      }
      commit('setUser', adminUser)
    },
    storeAuthenticatedUser ({ state, commit, dispatch }, { authUser }) {
      commit('setAuthUser', authUser)
      if (authUser && !authUser.emailVerified && !(router.currentRoute.name === 'accountConfirmation' || router.currentRoute.name === 'verify')) {
        console.log('loaded user with unverified email - redirecting to...')
        if (state.loginIntent && state.loginIntent.name === 'verify') router.replace(state.loginIntent)
        else router.replace({ name: 'accountConfirmation' })
      }

      if (authUser && authUser.uid) {
        functions.httpsCallable('recordUserLogin')({ userId: authUser.uid })
        dispatch('loadUser', { authUser })
      }
    },

    async checkDuplicatedUsername ({}, username) {
      const { data } = await functions.httpsCallable('checkUsernameIsFree')({ username })
      return data
    },
    async checkAuthRouteForEmail ({}, email) {
      const { data } = await functions.httpsCallable('checkAuthRouteForEmail')({ email })
      return data
    },
    async getEnterpriseUsers ({}, { accountId }) {
      const snapshot = await firebase
        .database()
        .ref('users')
        .orderByChild('enterprise/id')
        .equalTo(accountId)
        .once('value')
      const users = snapshot.val()
      return users
    },
    async loadEnterprise ({ state, commit }) {
      if (state.enterpriseRef) {
        // Make sure we only register one value listener at a time
        state.enterpriseRef.off()
      }

      if (state.user && state.user.enterprise) {
        const accountRef = firebase
          .database()
          .ref('enterprise')
          .child(state.user.enterprise.id)
        commit('setEnterpriseRef', accountRef)

        accountRef.on('value', snapshot => {
          const account = snapshot.val()
          commit('setEnterprise', account)
        })
      }
    },
    async preserveUserData({}, { userUid }) {
      try {
        const archiveUrl = await functions.httpsCallable('exportAllUserRelatedData')({ userUid })
        return archiveUrl
      } catch (error) {
        throw new Error(error.message || 'Data preservation request failed.')
      }
    },
    async deleteAccount ({ dispatch, getters }, { uid }) {
      try {
        await functions.httpsCallable('deleteAccount')({ uid })
        if (uid === getters.authUser.uid) dispatch('userSignOut')
        return
      } catch (error) {
        throw new Error(error.message || 'Account deletion failed')
      }
    }
  }
}
